import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import {
  Send,
  Trash2,
  Eye,
  MessageSquare,
  Image,
  Video,
  Music,
} from "lucide-react";
import { Message } from "../../types/generated";
import { useTranslation } from "react-i18next";

interface MessageCardProps {
  message: Message;
  onSendClick?: (id: number) => void;
  onDeleteClick?: (id: number) => void;
  isDraft?: boolean;
}

export const MessageCard = ({
  message,
  onSendClick,
  onDeleteClick,
  isDraft = false,
}: MessageCardProps) => {
  const { t } = useTranslation();
  
  return (
    <div className="flex flex-col p-4 border rounded-lg hover:bg-accent/50 transition-colors h-[160px]">
      <div className="flex flex-col justify-between h-full">
        <div>
          <div className="flex items-center gap-2 flex-wrap mb-3">
            <Badge
              variant="outline"
              className="capitalize flex items-center gap-1"
            >
              {message.content.type === "text" && (
                <MessageSquare className="h-3 w-3" />
              )}
              {message.content.type === "image" && (
                <Image className="h-3 w-3" />
              )}
              {message.content.type === "video" && (
                <Video className="h-3 w-3" />
              )}
              {message.content.type === "audio" && (
                <Music className="h-3 w-3" />
              )}
              {t(`message.content_type.${message.content.type}`)}
            </Badge>
            <Badge
              variant={
                message.status === "sent"
                  ? "success"
                  : message.status === "scheduled"
                  ? "default"
                  : "secondary"
              }
            >
              {t(`message.status.${message.status}`)}
            </Badge>
            {(false && message.metrics?.views) > 0 && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <Eye className="h-3 w-3" />
                <span>{message.metrics.views}</span>
              </div>
            )}
          </div>
          <p className="font-medium line-clamp-1 mb-2">
            {message.content.text?.body || t("message.no_content")}
          </p>
          <p className="text-xs text-muted-foreground">
            {message.scheduled
              ? t("message.scheduled_for", {
                  date: new Date(message.scheduled).toLocaleString(
                    "he-IL",
                    {
                      timeZone: "Asia/Jerusalem",
                      timeStyle: "short",
                      dateStyle: "short",
                    }
                  )
                })
              : message.sent_at
              ? t("message.sent_at", {
                  date: new Date(message.sent_at).toLocaleString("he-IL", {
                    timeZone: "Asia/Jerusalem",
                    timeStyle: "short",
                    dateStyle: "short",
                  })
                })
              : t("message.created_at", {
                  date: new Date(message.created_at).toLocaleString("he-IL", {
                    timeZone: "Asia/Jerusalem",
                    timeStyle: "short",
                    dateStyle: "short",
                  })
                })}
          </p>
        </div>
        <div className="flex items-center gap-2 justify-end">
          <div className="flex gap-2">
            {(isDraft || message.status === "scheduled") && onSendClick && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onSendClick(message.id)}
                className="hover:bg-green-100"
                aria-label={t("message.send")}
              >
                <Send className="h-4 w-4" />
              </Button>
            )}
            {onDeleteClick && (isDraft || message.status === "scheduled") && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onDeleteClick(message.id)}
                className="hover:bg-red-100"
                aria-label={t("message.delete")}
              >
                <Trash2 className="h-4 w-4 text-red-500" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
